import React, { Fragment, useState } from 'react';
import { Button, Card, Col, Form, Row, Alert } from 'react-bootstrap';
import * as yup from 'yup';
import PageHeader from '../../../components/pages/pageHeader';
import { useFormik } from 'formik';
import Divider from '../../../components/common/Divider';
import axios from 'axios';
import { baseUrl } from '../../../config';
import AlertErrorGetData from '../../../components/alert/errorGetData';
import Swal from 'sweetalert2';
import FileUploadDropzone from '../../../components/common/FileUploadDropzone';

const SolicitacoesMassa = () => {
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFilesChange = updatedFiles => {
    setFiles(updatedFiles);
    formik.setFieldValue('files', updatedFiles);
  };

  const sendBulkRequest = async (values, resetForm) => {
    setIsSubmitting(true);

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${sessionStorage.token}`,
          'Content-Type': 'multipart/form-data'
        }
      };

      const formData = new FormData();
      formData.append('observations', values.observations);
      formData.append('urgency', values.urgency);

      // Adicionar todos os arquivos ao FormData
      files.forEach((fileObj, index) => {
        formData.append(`files[${index}]`, fileObj.file);
      });

      const response = await axios.post(
        `${baseUrl}/cct/sendBulkLoadRequest`,
        formData,
        config
      );

      if (response.status === 200) {
        resetForm();
        setFiles([]);

        await Swal.fire({
          icon: 'success',
          title: 'Sucesso',
          text: 'Solicitação em massa enviada com sucesso.',
          confirmButtonText: 'Ok',
          confirmButtonColor: '#2b7be5'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar solicitação em massa:', error);
      AlertErrorGetData('Erro ao enviar solicitação em massa.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      observations: '',
      urgency: 'normal',
      files: []
    },
    validationSchema: yup.object({
      observations: yup
        .string()
        .required('As observações são obrigatórias.')
        .min(10, 'As observações devem ter pelo menos 10 caracteres.'),
      urgency: yup.string().required('A urgência é obrigatória.'),
      files: yup
        .array()
        .min(1, 'É necessário anexar pelo menos um arquivo.')
        .required('É necessário anexar pelo menos um arquivo.')
    }),
    onSubmit: (values, { resetForm }) => {
      sendBulkRequest(values, resetForm);
    }
  });

  return (
    <Fragment>
      <PageHeader
        title="Solicitações em Massa"
        description="Envie múltiplas solicitações de viagem através de planilhas ou documentos"
      />

      <Row className="g-3">
        <Col xs={12}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Nova Solicitação em Massa</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={formik.handleSubmit}>
                <Alert variant="info">
                  <strong>Instruções:</strong> Utilize este formulário para
                  enviar múltiplas solicitações de viagem. Anexe planilhas
                  (Excel, CSV) ou documentos com as informações das cargas.
                  Nossa equipe processará as informações e entrará em contato em
                  até 24 horas.
                </Alert>

                <Divider className="my-4">Informações da Solicitação</Divider>

                <Row>
                  <Col xs={12} lg={6} className="mb-3">
                    <Form.Label>
                      Urgência <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="urgency"
                      id="urgency"
                      value={formik.values.urgency}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={
                        formik.touched.urgency && formik.errors.urgency
                          ? 'is-invalid'
                          : ''
                      }
                    >
                      <option value="baixa">Baixa</option>
                      <option value="normal">Normal</option>
                      <option value="alta">Alta</option>
                      <option value="urgente">Urgente</option>
                    </Form.Select>
                    {formik.touched.urgency && formik.errors.urgency && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.urgency}
                      </div>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} className="mb-3">
                    <Form.Label>
                      Observações <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="observations"
                      id="observations"
                      placeholder="Descreva detalhes importantes sobre as solicitações, requisitos especiais, prazos, etc."
                      value={formik.values.observations}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={
                        formik.touched.observations &&
                        formik.errors.observations
                          ? 'is-invalid'
                          : ''
                      }
                    />
                    {formik.touched.observations &&
                      formik.errors.observations && (
                        <div className="invalid-feedback">
                          {formik.errors.observations}
                        </div>
                      )}
                    <Form.Text className="text-muted">
                      Mínimo 10 caracteres. Seja o mais específico possível.
                    </Form.Text>
                  </Col>
                </Row>

                <Divider className="my-4">Anexos</Divider>

                <Row>
                  <Col xs={12} className="mb-3">
                    <FileUploadDropzone
                      onFilesChange={handleFilesChange}
                      maxFiles={10}
                      maxSize={10485760}
                    />
                    {formik.touched.files && formik.errors.files && (
                      <div className="text-danger mt-2">
                        <small>{formik.errors.files}</small>
                      </div>
                    )}
                  </Col>
                </Row>

                <Divider className="my-4" />

                <Row>
                  <Col xs={12}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          formik.resetForm();
                          setFiles([]);
                        }}
                        disabled={isSubmitting}
                      >
                        Limpar
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || files.length === 0}
                      >
                        {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card bg="light">
            <Card.Body>
              <h6 className="fw-bold mb-3">Formatos Aceitos</h6>
              <ul className="mb-3">
                <li>Planilhas Excel (.xls, .xlsx)</li>
                <li>Arquivos CSV (.csv)</li>
                <li>Documentos Word (.doc, .docx)</li>
                <li>PDFs (.pdf)</li>
                <li>Imagens (.png, .jpg, .jpeg, .gif)</li>
              </ul>

              <h6 className="fw-bold mb-3">Dicas</h6>
              <ul className="mb-0">
                <li>
                  Para melhor processamento, utilize planilhas com colunas
                  claras: Remetente, Destinatário, Origem, Destino, Peso, Data
                  de Carregamento, Data de Entrega
                </li>
                <li>
                  Certifique-se de que todas as informações estão completas e
                  legíveis
                </li>
                <li>
                  Use o campo de observações para informar detalhes adicionais
                  ou instruções especiais
                </li>
                <li>
                  Nossa equipe processará as solicitações e entrará em contato
                  em até 24 horas
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default SolicitacoesMassa;
